import React, {useContext, useEffect, useState} from "react";
import {Box, Button, ButtonGroup, Modal, Spinner, Text} from "@nike/eds";
import {DSLeftCWService} from "./DSLeftCWService";
import SnackbarContext from "../../providers/snackbar-context";
import {edsSpace16} from "@nike/eds/dist/tokens";
import SubmitIcon from '../shared/rocket';
import {Type} from "./Type";
import "./DSLeftCWConfirmModal.css";
import {Shipment, Status} from "../../graphql/ds-left-cw-api/tant/api";
import {mapInventoryLocation} from "./LocationMapper";

enum Step {
    LOADING,
    CONFIRM,
    NOT_FOUND,
    SUCCESS,
}

interface DSLeftCWConfirmModalProps {
    isOpen: boolean;
    onClose: (isConfirmed: boolean) => void;
    type: Type;
    location: string;
    shipmentIds: string[];
    reason: string;
    comment: string;
    dSLeftCWService: DSLeftCWService;
}

function DSLeftCWConfirmModal({
                                  isOpen,
                                  onClose,
                                  type,
                                  location,
                                  shipmentIds,
                                  reason,
                                  comment,
                                  dSLeftCWService,
                              }: DSLeftCWConfirmModalProps) {
    const snackbarCtx = useContext(SnackbarContext);
    const [step, setStep] = useState<Step>(Step.LOADING);
    const [processing, setProcessing] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [foundShipments, setFoundShipments] = useState<any[]>([]);
    const [notFoundShipmentIds, setNotFoundShipmentIds] = useState<string[]>([]);

    useEffect(() => {
        if (isOpen) {

            if (shipmentIds.length === 0) {
                setFoundShipments([]);
                setNotFoundShipmentIds([]);
                setStep(Step.NOT_FOUND);
                return;
            }

            setStep(Step.LOADING);
            dSLeftCWService.findShipmentsById(shipmentIds)
                .then(result => {
                    const response = result.data?.findShipmentsById;

                    if (!response) {
                        throw new Error("No data returned from query.");
                    }

                    const found = response.found;
                    const foundIds = found.map((s: any) => s.shipmentNumber);
                    const notFoundIds = shipmentIds.filter(id => !foundIds.includes(id));

                    setFoundShipments(found);
                    setNotFoundShipmentIds(notFoundIds);
                    setStep(notFoundIds.length > 0 ? Step.NOT_FOUND : Step.CONFIRM);
                })
                .catch(error => {
                    snackbarCtx.displayMsg("Error loading shipment info: " + error, "error");
                    setStep(Step.NOT_FOUND);
                });
        }
    }, [isOpen, dSLeftCWService, shipmentIds, snackbarCtx]);

    const handleSubmit = () => {
        setProcessing(true);

        const shipments : Shipment[] = foundShipments.map((s) => ({
            shipmentId: s.shipmentNumber,
            containerNumber: s.containerNumber,
            billOfLading: s.bolNumber,
        }));

        dSLeftCWService.submitDSLeftCW(location, reason, comment, shipments)
            .then(response => {
                setProcessing(false);
                const result = response.data.submitManualTantCorrection;
                if (result?.status === Status.SUCCESS) {
                    setConfirmed(true);
                    setStep(Step.SUCCESS);
                } else {
                    snackbarCtx.displayMsg("Failed to submit event: " + response.message, "error");
                }
            })
            .catch(error => {
                setProcessing(false);
                snackbarCtx.displayMsg("Failed to submit event: " + error, "error");
            });
    };

    const StepTitles: Record<Step, string> = {
        [Step.LOADING]: "Loading",
        [Step.CONFIRM]: "Confirm event",
        [Step.NOT_FOUND]: "Shipment Info not found",
        [Step.SUCCESS]: "DirectShipmentLeftCustomsWarehouse event(s) triggered",
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={() => onClose(confirmed)}
            headerSlot={StepTitles[step]}
            footerSlot={
                step === Step.SUCCESS ? undefined :
                    step === Step.CONFIRM ? (
                        processing ? <Spinner size="medium" /> : (
                            <ButtonGroup>
                                <Button variant="primary" onClick={handleSubmit}>Confirm</Button>
                                <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
                            </ButtonGroup>
                        )
                    ) : (
                        <Button variant="primary" onClick={() => onClose(false)}>Close</Button>
                    )
            }
        >
            <Box>
                {step === Step.LOADING && (
                    <Spinner size="large" />
                )}

                {step === Step.CONFIRM && (
                    <>
                        <Text>
                            Are you sure you want to trigger a DirectShipmentLeftCustomsWarehouse event for the following shipment(s)?
                        </Text>
                        <Text>(SAP Shipment ID - TransportHolderNumber - BOL)</Text>
                        <ul style={{marginTop: edsSpace16}}>
                            {foundShipments.map((shipment, index) => (
                                <li key={index}>
                                    {shipment.shipmentNumber} - {shipment.containerNumber} - {shipment.bolNumber}
                                </li>
                            ))}
                        </ul>
                        <Text font={"title-6"} style={{marginTop: edsSpace16}}>Additional information:</Text>
                        <ul>
                            <li>{type}: {mapInventoryLocation(location)}</li>
                            <li>Reason: {reason}</li>
                            <li>Comment: {comment}</li>
                        </ul>
                    </>
                )}

                {step === Step.NOT_FOUND && (
                    <>
                        <Text>
                            No shipment info found for SAP Shipment ID{notFoundShipmentIds.length > 1 ? "s" : ""} = <strong>{notFoundShipmentIds.join(", ")}</strong>
                        </Text>
                        <Text>
                            Please check if the SAP Shipment ID{notFoundShipmentIds.length > 1 ? "s are" : " is"} valid or contact support for assistance.
                        </Text>
                    </>
                )}

                {step === Step.SUCCESS && (
                    <>
                        <Text>
                            Please note: processing the event will take some time. Go to the overview screen and refresh the page.
                        </Text>
                        <div style={{marginTop: edsSpace16, textAlign: "center"}}>
                            <SubmitIcon />
                        </div>
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default DSLeftCWConfirmModal;